import * as React from 'react';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { Box, Button } from '@mui/material';
import { apiService } from '../../../../api/apiService';
import { PublicClientApplication } from '@azure/msal-browser';

interface FilePreviewProps {
    loading: boolean;
    error: string | null;
    name: string;
    docPath?: string;
    instance?: PublicClientApplication;
}

const DownloadFile: React.FC<FilePreviewProps> = ({ loading, error, name, docPath, instance }) => {
    const [downloading, setDownloading] = React.useState(false);

    const handleDownload = async () => {
        if (!docPath) {
            console.error("No document path available for download.");
            return;
        }

        setDownloading(true);
        try {
            const response = await apiService(
                `/download?doc_path=${encodeURIComponent(docPath)}`, 
                'GET', 
                undefined, 
                undefined, 
                instance
            );

            if (response.data) {
                // Convert Base64 to a Blob
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                
                // Determine MIME type from file extension
                const fileExtension = name.split('.').pop()?.toLowerCase();
                let mimeType = 'application/octet-stream';
                
                if (fileExtension === 'pdf') mimeType = 'application/pdf';
                else if (fileExtension === 'docx') mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                else if (fileExtension === 'doc') mimeType = 'application/msword';
                else if (fileExtension === 'xlsx') mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                else if (fileExtension === 'xls') mimeType = 'application/vnd.ms-excel';
                else if (fileExtension === 'xlsm') mimeType = 'application/vnd.ms-excel.sheet.macroEnabled.12';
                else if (fileExtension === 'pptx') mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
                else if (fileExtension === 'ppt') mimeType = 'application/vnd.ms-powerpoint';
                else if (fileExtension === 'jpg' || fileExtension === 'jpeg') mimeType = 'image/jpeg';
                else if (fileExtension === 'png') mimeType = 'image/png';
                
                const blob = new Blob([byteArray], { type: mimeType });

                // Create a download link and trigger the download
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = name || response.file_name || 'download';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }
        } catch (err) {
            console.error("Error downloading file:", err);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <DialogContent sx={{ 
            overflowY: 'hidden', 
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
            {loading ? (
                <div style={{ textAlign: 'center' }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading file info...
                    </Typography>
                </div>
            ) : error ? (
                <Typography color="error" variant="body2">
                    {error}
                </Typography>
            ) : (
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                    {name.split('_').slice(-1)[0]}
                    </Typography>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={handleDownload}
                        disabled={!docPath || downloading}
                    >
                        {downloading ? 'Downloading...' : 'Download File'}
                    </Button>
                </Box>
            )}
        </DialogContent>
    );
};

export default DownloadFile;